<template>
  <div class="document-viewer-popup">
    <div class="viewer-container">
      <div class="viewer-content" ref="viewerContent">
        <div v-show="loading" class="viewer-loading">
          <div class="spinner"></div>
          <p>Загрузка документа...</p>
        </div>

        <div v-show="error" class="viewer-error">
          <p class="error-message">{{ error }}</p>
          <button @click="retryLoad" class="retry-button">Попробовать снова</button>
          <details class="error-details">
            <summary>Техническая информация</summary>
            <pre>{{ debugInfo }}</pre>
          </details>
        </div>

        <div
          class="viewer-canvas-container"
          v-show="!loading && !error"
          ref="canvasContainer"
          @contextmenu.prevent
        >
          <div
            class="viewer-canvas-wrapper"
            :style="{ transform: `scale(${scale})`, transformOrigin: 'center top' }"
          >
            <canvas
              ref="documentCanvas"
              class="document-canvas"
              @contextmenu.prevent
            ></canvas>
            <!-- Watermark overlay -->
            <div class="canvas-watermark" ref="watermarkLayer"></div>
          </div>
        </div>
      </div>

      <!-- Protection overlay -->
      <div class="screenshot-protection" ref="protectionLayer"></div>
    </div>

    <!-- Zoom controls -->
    <div class="viewer-zoom-controls" v-show="!loading && !error">
      <button
        @click="zoomOut"
        :disabled="scale <= minScale"
        class="control-btn control-btn-zoom"
        title="Уменьшить"
      >
        <span>−</span>
      </button>
      <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
      <button
        @click="zoomIn"
        :disabled="scale >= maxScale"
        class="control-btn control-btn-zoom"
        title="Увеличить"
      >
        <span>+</span>
      </button>
      <button
        @click="resetZoom"
        class="control-btn control-btn-reset"
        title="Сбросить масштаб"
      >
        <span>⟲</span>
      </button>
    </div>

    <!-- Page navigation controls -->
    <div class="viewer-controls" v-show="!loading && !error && totalPages > 1">
      <button
        @click="previousPage"
        :disabled="currentPage === 1"
        class="control-btn control-btn-arrow"
        title="Предыдущая страница"
      >
        <svg width="24" height="40" viewBox="0 0 24 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 4L4 20L20 36" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <span class="page-info">
        {{ currentPage }} / {{ totalPages }}
      </span>
      <button
        @click="nextPage"
        :disabled="currentPage === totalPages"
        class="control-btn control-btn-arrow"
        title="Следующая страница"
      >
        <svg width="24" height="40" viewBox="0 0 24 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 4L20 20L4 36" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import getSecureFileUrl from '@/tools/getSecureFileUrl';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default {
  name: 'DocumentViewerPopup',
  props: {
    document: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      currentPage: 1,
      totalPages: 1,
      scale: 1,
      minScale: 0.5,
      maxScale: 3,
      pdfDoc: null,
      debugInfo: '',
      renderTask: null,
      secureDocumentUrl: null, // Store the secure URL with token
    };
  },
  computed: {
    documentUrl() {
      // Return secure URL if available
      if (this.secureDocumentUrl) {
        return this.secureDocumentUrl;
      }
      return null;
    },
    fileExtension() {
      // First try to get extension from document object
      if (this.document) {
        // Check for ext property (e.g., ".pdf")
        if (this.document.ext) {
          const ext = this.document.ext.replace('.', '').toLowerCase();
          if (process.env.NODE_ENV === 'development') {
            console.log('File extension from document.ext:', ext);
          }
          return ext;
        }

        // Check for mime type (e.g., "application/pdf")
        if (this.document.mime) {
          const mimeMap = {
            'application/pdf': 'pdf',
            'image/jpeg': 'jpg',
            'image/jpg': 'jpg',
            'image/png': 'png',
            'image/gif': 'gif',
            'image/webp': 'webp',
          };
          const ext = mimeMap[this.document.mime.toLowerCase()];
          if (ext) {
            if (process.env.NODE_ENV === 'development') {
              console.log('File extension from document.mime:', ext);
            }
            return ext;
          }
        }
      }

      // Fallback: try to extract from URL
      if (!this.documentUrl) return null;
      const urlPath = this.documentUrl.split('?')[0];
      const ext = urlPath.split('.').pop().toLowerCase();
      if (process.env.NODE_ENV === 'development') {
        console.log('File extension from URL:', ext);
      }
      return ext;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initViewer();
      this.setupProtection();
    });
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    log(...args) {
      if (process.env.NODE_ENV === 'development') {
        console.log(...args);
      }
    },

    logError(...args) {
      if (process.env.NODE_ENV === 'development') {
        console.error(...args);
      }
    },

    logWarn(...args) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(...args);
      }
    },

    async initViewer() {
      try {
        this.loading = true;
        this.error = null;
        this.debugInfo = '';

        this.log('Document object:', this.document);

        this.debugInfo = `Document: ${JSON.stringify(this.document, null, 2)}`;

        if (!this.document) {
          throw new Error('Документ не найден');
        }

        // Get secure URL with token
        this.log('Fetching secure URL...');
        this.secureDocumentUrl = await getSecureFileUrl(this.document);
        this.log('Secure URL obtained:', this.secureDocumentUrl);

        this.debugInfo += `\nSecure URL: ${this.secureDocumentUrl}`;

        const ext = this.fileExtension;
        this.log('File extension:', ext);

        if (ext === 'pdf') {
          await this.loadPDF();
        } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
          await this.loadImage();
        } else {
          throw new Error(`Неподдерживаемый формат файла: ${ext}. Поддерживаются: PDF, JPG, PNG, GIF, WEBP`);
        }

        this.loading = false;
      } catch (err) {
        this.logError('Error loading document:', err);
        this.error = err.message || 'Ошибка загрузки документа';
        this.debugInfo += `\n\nError: ${err.stack || err.message}`;
        this.loading = false;
      }
    },

    retryLoad() {
      this.initViewer();
    },

    async loadPDF() {
      try {
        this.log('Loading PDF from:', this.documentUrl);

        // Load PDF as blob to bypass X-Frame-Options
        const response = await fetch(this.documentUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();

        this.log('PDF fetched, loading document...');

        const loadingTask = pdfjsLib.getDocument({
          data: arrayBuffer,
        });

        this.pdfDoc = await loadingTask.promise;
        this.totalPages = this.pdfDoc.numPages;

        this.log('PDF loaded, total pages:', this.totalPages);

        await this.$nextTick();
        await this.renderPage(this.currentPage);
      } catch (error) {
        this.logError('PDF loading error:', error);
        throw new Error(`Ошибка загрузки PDF: ${error.message}`);
      }
    },

    async renderPage(pageNum) {
      if (!this.pdfDoc) {
        this.logError('PDF document not loaded');
        return;
      }

      // Cancel previous render task if exists
      if (this.renderTask) {
        try {
          await this.renderTask.cancel();
        } catch (e) {
          // Ignore cancellation errors
        }
      }

      await this.$nextTick();

      const canvas = this.$refs.documentCanvas;
      if (!canvas) {
        this.logError('Canvas element not found');
        return;
      }

      this.log('Rendering page:', pageNum);

      try {
        const page = await this.pdfDoc.getPage(pageNum);
        this.log('Page loaded');

        const ctx = canvas.getContext('2d');

        // Calculate viewport with base scale for high quality
        const baseScale = this.getResponsiveScale();
        const viewport = page.getViewport({ scale: baseScale });

        this.log('Viewport size:', viewport.width, 'x', viewport.height);

        // Set canvas dimensions
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const renderContext = {
          canvasContext: ctx,
          viewport,
        };

        this.log('Starting render...');
        this.renderTask = page.render(renderContext);
        await this.renderTask.promise;
        this.renderTask = null;
        this.log('Render complete');

        // Scroll to top after page change
        this.scrollToTop();

        // Update watermark
        this.updateWatermark();

        // Auto-fit to screen on first render for mobile
        if (pageNum === 1) {
          this.autoFitToScreen();
        }
      } catch (error) {
        if (error.name === 'RenderingCancelledException') {
          this.log('Rendering cancelled');
          return;
        }
        this.logError('Render error:', error);
        throw error;
      }
    },

    async loadImage() {
      const canvas = this.$refs.documentCanvas;
      if (!canvas) {
        this.logError('Canvas element not found');
        return;
      }

      const ctx = canvas.getContext('2d');

      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve, reject) => {
        img.onload = () => {
          // Set canvas to image natural size
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          ctx.drawImage(img, 0, 0);
          resolve();
        };
        img.onerror = (error) => {
          this.logError('Image load error:', error);
          reject(new Error('Не удалось загрузить изображение'));
        };
        img.src = this.documentUrl;
      });

      this.totalPages = 1;
      this.updateWatermark();
      this.autoFitToScreen();
    },

    getResponsiveScale() {
      // Return appropriate base scale based on viewport
      const width = window.innerWidth;
      if (width < 768) {
        return 1.2; // Mobile
      }
      if (width < 1024) {
        return 1.5; // Tablet
      }
      return 2; // Desktop - higher quality
    },

    autoFitToScreen() {
      // Automatically adjust scale to fit document within viewport
      this.$nextTick(() => {
        const canvas = this.$refs.documentCanvas;
        const container = this.$refs.canvasContainer;

        if (!canvas || !container) return;

        const containerWidth = container.clientWidth;
        const containerPadding = window.innerWidth < 768 ? 20 : 40; // padding from CSS
        const availableWidth = containerWidth - containerPadding;

        const canvasWidth = canvas.width;

        // If canvas is wider than available space, scale it down
        if (canvasWidth > availableWidth) {
          const fitScale = availableWidth / canvasWidth;
          this.scale = Math.max(fitScale * 0.95, this.minScale); // 95% for some breathing room
          this.log('Auto-fit scale applied:', this.scale);
        }
      });
    },

    scrollToTop() {
      const container = this.$refs.canvasContainer;
      if (container) {
        container.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      }
    },

    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
        if (this.fileExtension === 'pdf') {
          await this.renderPage(this.currentPage);
        }
      }
    },

    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
        if (this.fileExtension === 'pdf') {
          await this.renderPage(this.currentPage);
        }
      }
    },

    async zoomIn() {
      if (this.scale < this.maxScale) {
        this.scale = Math.min(this.scale + 0.25, this.maxScale);
        this.log('Zoom in to:', this.scale);
      }
    },

    async zoomOut() {
      if (this.scale > this.minScale) {
        this.scale = Math.max(this.scale - 0.25, this.minScale);
        this.log('Zoom out to:', this.scale);
      }
    },

    async resetZoom() {
      this.scale = 1;
      this.scrollToTop();
      this.log('Reset zoom');
    },

    setupProtection() {
      // Block context menu
      document.addEventListener('contextmenu', this.preventContextMenu);

      // Block screenshot hotkeys
      document.addEventListener('keydown', this.preventScreenshotKeys);

      // Block text selection
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';

      // Add watermarks
      this.addWatermark();

      // Detect screenshot attempts (partial protection)
      this.detectScreenshot();
    },

    removeProtection() {
      document.removeEventListener('contextmenu', this.preventContextMenu);
      document.removeEventListener('keydown', this.preventScreenshotKeys);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';

      if (this.screenshotInterval) {
        clearInterval(this.screenshotInterval);
      }
    },

    cleanup() {
      this.removeProtection();

      // Cancel any pending render task
      if (this.renderTask) {
        try {
          this.renderTask.cancel();
        } catch (e) {
          // Ignore
        }
      }

      // Clean up PDF document
      if (this.pdfDoc) {
        this.pdfDoc.destroy();
        this.pdfDoc = null;
      }
    },

    preventContextMenu(e) {
      e.preventDefault();
      return false;
    },

    preventScreenshotKeys(e) {
      // Block Print Screen, Cmd+Shift+3/4/5 (Mac), Win+Shift+S (Windows)
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

      if (
        e.key === 'PrintScreen'
        || (isMac && e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key))
        || (!isMac && e.metaKey && e.shiftKey && e.key === 'S')
        || (e.ctrlKey && e.key === 'p') // Block print
      ) {
        e.preventDefault();
        // eslint-disable-next-line no-alert
        alert('Скриншоты и печать документа запрещены');
        return false;
      }
      return true;
    },

    addWatermark() {
      const { protectionLayer } = this.$refs;
      if (protectionLayer) {
        const watermarkText = 'SKYPRO.SU '.repeat(20);
        protectionLayer.setAttribute('data-watermark', watermarkText);
      }
    },

    updateWatermark() {
      const { watermarkLayer } = this.$refs;
      if (watermarkLayer) {
        const watermarkText = 'SKYPRO.SU '.repeat(15);
        watermarkLayer.setAttribute('data-watermark', watermarkText);
      }
    },

    detectScreenshot() {
      // Detect focus loss (may indicate screenshot attempt)
      this.screenshotInterval = setInterval(() => {
        if (document.hidden || !document.hasFocus()) {
          this.logWarn('Possible screenshot attempt detected');
        }
      }, 100);
    },
  },
};
</script>

<style lang="scss" scoped>
.document-viewer-popup {
  display: flex;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  @include wRule(
    padding,
    (
      xl: 40 0,
      lg: 40 0,
      md: 40 0,
      sm: 105 0,
    )
  );
}

.viewer-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 100%;
}

.viewer-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  min-height: 0;
  padding-top: 100px;

  @include screen("sm", max) {
    padding-top: 80px;
  }
}

.viewer-loading,
.viewer-error {
  text-align: center;
  color: #fff;
  padding: 40px 20px;
}

.error-message {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #ff6b6b;
}

.retry-button {
  background: var(--corp_color, #2A5388);
  color: #fff;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
}

.error-details {
  margin-top: 20px;
  text-align: left;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  summary {
    cursor: pointer;
    font-weight: 600;
    padding: 10px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    color: #fff;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  }

  pre {
    margin-top: 10px;
    padding: 15px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    overflow: auto;
    font-size: 12px;
    max-height: 300px;
    color: #fff;
  }
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top: 4px solid var(--corp_color, #2A5388);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.viewer-canvas-container {
  width: 100%;
  height: 100%;
  overflow: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  position: relative;

  // Custom scrollbar styling
  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 5px;

    &:hover {
      background: rgba(255, 255, 255, 0.4);
    }
  }
}

.viewer-canvas-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease-out;
  transform-origin: center top;
  max-width: 100%;
  height: fit-content;
  padding: 20px;
  box-sizing: border-box;

  @include screen("sm", max) {
    padding: 10px;
  }
}

.document-canvas {
  display: block;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  user-select: none;
  -webkit-user-select: none;
  pointer-events: none;
  background: #fff;
  margin: 0 auto;
}

.canvas-watermark {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;

  &::before {
    content: attr(data-watermark);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 60px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.06);
    white-space: nowrap;
    user-select: none;
    pointer-events: none;
    letter-spacing: 20px;
    max-width: none;

    @include screen("sm", max) {
      font-size: 30px;
      letter-spacing: 10px;
    }
  }

  &::after {
    content: attr(data-watermark);
    position: absolute;
    top: 30%;
    left: 60%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 60px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.06);
    white-space: nowrap;
    user-select: none;
    pointer-events: none;
    letter-spacing: 20px;
    max-width: none;

    @include screen("sm", max) {
      font-size: 30px;
      letter-spacing: 10px;
    }
  }
}

.viewer-controls,
.viewer-zoom-controls {
  display: flex;
  align-items: center;
  gap: 30px;
  padding: 15px 30px;
  pointer-events: auto;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50px;
  backdrop-filter: blur(10px);

  @include screen("sm", max) {
    gap: 15px;
    padding: 10px 20px;
  }
}

.viewer-controls {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;

  @include screen("sm", max) {
    bottom: 60px;
  }
}

.viewer-zoom-controls {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;

  @include screen("sm", max) {
    top: 15px;
  }
}

.control-btn {
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;

  &:hover:not(:disabled) {
    color: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &.control-btn-arrow {
    width: 60px;
    height: 60px;

    @include screen("sm", max) {
      width: 50px;
      height: 50px;
    }

    svg {
      width: 24px;
      height: 40px;

      @include screen("sm", max) {
        width: 20px;
        height: 32px;
      }
    }
  }

  &.control-btn-zoom,
  &.control-btn-reset {
    width: 50px;
    height: 50px;
    font-size: 32px;
    font-weight: 300;
    line-height: 1;

    @include screen("sm", max) {
      width: 40px;
      height: 40px;
      font-size: 28px;
    }

    span {
      display: block;
    }
  }

  &.control-btn-reset {
    font-size: 28px;

    @include screen("sm", max) {
      font-size: 24px;
    }
  }
}

.page-info,
.zoom-info {
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  min-width: 80px;
  text-align: center;

  @include screen("sm", max) {
    font-size: 16px;
    min-width: 60px;
  }
}

.screenshot-protection {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999;
  overflow: hidden;

  &::before {
    content: attr(data-watermark);
    position: absolute;
    top: 20%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 100px;
    font-weight: 900;
    color: rgba(255, 255, 255, 0.05);
    white-space: nowrap;
    user-select: none;
    pointer-events: none;
    letter-spacing: 30px;
    max-width: none;

    @include screen("sm", max) {
      font-size: 50px;
      letter-spacing: 15px;
    }
  }

  &::after {
    content: attr(data-watermark);
    position: absolute;
    bottom: 20%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 100px;
    font-weight: 900;
    color: rgba(255, 255, 255, 0.05);
    white-space: nowrap;
    user-select: none;
    pointer-events: none;
    letter-spacing: 30px;
    max-width: none;

    @include screen("sm", max) {
      font-size: 50px;
      letter-spacing: 15px;
    }
  }
}
</style>
